// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/eventAccess.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// shared/eventAccess.js
//
// The canonical answer to "what may this viewer see and do on this event?"
//
// ── The divergence this resolves ───────────────────────────────────────────
//
// App — services/eventAccessService.js :: getEventAccessInfo()
//     host or cohost      -> full access
//     privacy 'public'    -> full access
//     privacy 'private'   -> full access IF ON THE GUEST LIST
//     privacy 'approval'  -> full access if the access request is approved
//
// Web — src/pages/EventInvitation.js :: canViewDetails()
//     host or cohost      -> full access
//     privacy 'public'    -> full access
//     privacy 'private'   -> full access IF THE PHONE IS VERIFIED
//     privacy 'approval'  -> full access if the access request is approved
//
// For a private event the two clients asked completely different questions. A
// guest on the host's list saw the invitation in the app and a locked page on
// the web; a guest who had verified their phone but was never invited saw the
// opposite. Neither client was wrong on its own terms — there simply was no
// single definition of the rule.
//
// Both signals are legitimate and they answer different questions:
//   guest list  = "were you invited?"        (authorisation)
//   phone       = "are you who you claim?"   (authentication)
//
// The canonical rule below requires the guest list, and treats phone
// verification as an ADDITIONAL requirement only when the event asks for it
// (`requires_phone_verification`). That matches the host's mental model: the
// guest list is the invitation, phone verification is an anti-gatecrasher
// measure layered on top.
//
// Pure: no imports, no I/O. Both clients pass in the facts they have.

/**
 * @typedef {{
 *   isHost?: boolean,
 *   isCohost?: boolean,
 *   privacy?: 'public' | 'private' | 'approval' | string,
 *   onGuestList?: boolean,
 *   phoneVerified?: boolean,
 *   requiresPhoneVerification?: boolean,
 *   approvalStatus?: 'pending' | 'approved' | 'rejected' | null,
 * }} AccessFacts
 *
 * @typedef {{
 *   fullAccess: boolean,
 *   canViewSummary: boolean,
 *   isHost: boolean,
 *   isCohost: boolean,
 *   reason: string,
 *   needs: 'nothing' | 'guestList' | 'phoneVerification' | 'approval' | 'approvalPending' | 'approvalRejected',
 * }} AccessVerdict
 */

/**
 * @param {AccessFacts} facts
 * @returns {AccessVerdict}
 */
export function resolveEventAccess(facts = {}) {
  const {
    isHost = false,
    isCohost = false,
    privacy = 'public',
    onGuestList = false,
    phoneVerified = false,
    requiresPhoneVerification = false,
    approvalStatus = null,
  } = facts;

  const base = { isHost: !!isHost, isCohost: !!isCohost, canViewSummary: true };

  // Running the event always wins, regardless of every other signal.
  if (isHost || isCohost) {
    return { ...base, fullAccess: true, reason: 'host', needs: 'nothing' };
  }

  if (privacy === 'public') {
    // A public event can still demand phone verification before revealing
    // location and letting someone RSVP.
    if (requiresPhoneVerification && !phoneVerified) {
      return { ...base, fullAccess: false, reason: 'phoneRequired', needs: 'phoneVerification' };
    }
    return { ...base, fullAccess: true, reason: 'public', needs: 'nothing' };
  }

  if (privacy === 'private') {
    // Authorisation first: being verified does not mean being invited. This is
    // the half the web client was missing.
    if (!onGuestList) {
      return { ...base, fullAccess: false, reason: 'notOnGuestList', needs: 'guestList' };
    }
    // Then authentication, only if the host asked for it. This is the half the
    // app client was missing.
    if (requiresPhoneVerification && !phoneVerified) {
      return { ...base, fullAccess: false, reason: 'phoneRequired', needs: 'phoneVerification' };
    }
    return { ...base, fullAccess: true, reason: 'onGuestList', needs: 'nothing' };
  }

  if (privacy === 'approval') {
    if (approvalStatus === 'approved') {
      if (requiresPhoneVerification && !phoneVerified) {
        return { ...base, fullAccess: false, reason: 'phoneRequired', needs: 'phoneVerification' };
      }
      return { ...base, fullAccess: true, reason: 'approved', needs: 'nothing' };
    }
    if (approvalStatus === 'pending') {
      return { ...base, fullAccess: false, reason: 'approvalPending', needs: 'approvalPending' };
    }
    if (approvalStatus === 'rejected') {
      return { ...base, fullAccess: false, reason: 'approvalRejected', needs: 'approvalRejected' };
    }
    return { ...base, fullAccess: false, reason: 'approvalRequired', needs: 'approval' };
  }

  // Unknown privacy value: deny full access but still show the summary, and say
  // so explicitly rather than falling through a bare `return false`.
  return { ...base, fullAccess: false, reason: 'unknownPrivacy', needs: 'approval' };
}

/**
 * Adapts a verdict into the shape resolveRsvpGate expects, so a client never
 * hand-rolls the mapping.
 */
export function verdictToGateAccess(verdict) {
  if (!verdict) return null;
  return {
    fullAccess: verdict.fullAccess,
    isHost: verdict.isHost,
    isCohost: verdict.isCohost,
    requiresGuestList: verdict.needs === 'guestList',
    requiresApproval:
      verdict.needs === 'approval' ||
      verdict.needs === 'approvalPending' ||
      verdict.needs === 'approvalRejected',
    accessStatus:
      verdict.needs === 'approvalPending' ? 'pending'
      : verdict.needs === 'approvalRejected' ? 'rejected'
      : verdict.fullAccess ? 'approved'
      : null,
  };
}
